import type * as React from 'react';
import { graphql } from 'react-apollo';

import gql from 'graphql-tag';
import type { ComponentEnhancer } from 'recompose';
import { branch, compose } from 'recompose';

import waitFor from 'js/lib/waitFor';

import FlexSupportExperiments from 'bundles/epic/clients/FlexSupport';

import type { ReplaceCustomContent } from '../types/CustomLabels';
import type { CustomLabelsV2, CustomLabelsV2Connection } from './__generated__/CustomLabelsV2';
import customLabelsByDegreeIdDataProvider from './customLabelsByDegreeIdDataProvider';
import withCustomLabelsMapping from './withCustomLabelsMapping';

type CustomLabelsV2ByDegreeIdQuery = {
  CustomLabelsV2: {
    byDegreeId: CustomLabelsV2Connection | null;
  } | null;
};

type CustomLabelsV2ByDegreeIdQueryVariables = {
  degreeId: string;
  includes?: string | null;
};

type OuterProps = {
  degreeId: string;
};

type InnerProps = OuterProps & {
  customLabelsV2?: CustomLabelsV2;
  replaceCustomContent: ReplaceCustomContent;
};

/* eslint-disable graphql/template-strings */
const CustomLabelsMutation = gql`
  mutation CustomLabelsMutation($degreeId: String!, $body: DataMap) {
    byDegreeId(body: $body, degreeId: $degreeId)
      @rest(
        method: "POST"
        type: "CustomLabelsV2"
        path: "customLabels.V2/?action=byDegreeId&degreeId={args.degreeId}"
        bodyKey: "body"
      ) {
      id
      namings {
        courseraCourse
      }
    }
  }
`;
/* eslint-enable graphql/template-strings */

export default <PropsFromCaller extends OuterProps>(Component: React.ComponentType<PropsFromCaller & InnerProps>) =>
  compose<InnerProps & PropsFromCaller, PropsFromCaller>(
    branch(
      () => FlexSupportExperiments.get('degreeHomeCustomLabelsEnabled'),
      graphql<OuterProps, CustomLabelsV2ByDegreeIdQuery, CustomLabelsV2ByDegreeIdQueryVariables, InnerProps>(
        CustomLabelsMutation,
        {
          name: 'getCustomLabels',
        }
      )
    ),
    branch(
      () => FlexSupportExperiments.get('degreeHomeCustomLabelsEnabled'),
      customLabelsByDegreeIdDataProvider() as ComponentEnhancer<{}, {}>
    ),
    // We need to wait for the customLabelsV2 prop explicitly to prevent "flashing" behavior for the custom label.
    // This is needed because the degree home dashboard is rendered client side.
    branch(
      () => FlexSupportExperiments.get('degreeHomeCustomLabelsEnabled'),
      waitFor(({ customLabelsV2 }) => customLabelsV2 !== undefined)
    ),
    withCustomLabelsMapping
  )(Component);
